import { useCallback, useEffect, useState } from "react";
import type { ComponentType } from "react";
import {
  BarChart3, Check, Inbox, Link2, ShieldAlert, ShieldCheck,
} from "lucide-react";
import { adminApi } from "@/api/admin";
import type { AffAnomalie, AffInvito, AffKpi, AffRichiesta } from "@/types/admin";
import { formatDateTime } from "@/lib/dates";
import { copyText } from "@/lib/clipboard";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const MOTIVO_RETE: Record<string, string> = {
  incarico_gia_aperto: "agenzia già in carico a un altro responsabile",
  attivazione_senza_account: "scheda agenzia incompleta sul CRM",
  ingresso_in_rete_fallito: "errore durante l'ingresso in rete",
};

// Stati terminali "storicizzati" del backend (rifiutata/scaduta) più "attiva":
// il servizio rifiuta() sovrascrive lo stato senza guardia su "attiva", quindi
// nasconderla qui evita di rifiutare un'affiliazione già provisionata e attiva.
const STATI_NON_RIFIUTABILI = new Set(["rifiutata", "scaduta", "attiva"]);

// Etichette e colori per stato richiesta (il valore tecnico resta nel backend).
const STATO_BADGE: Record<string, { label: string; className: string }> = {
  dati_inseriti: { label: "Da verificare PEC", className: "border-border text-foreground/70" },
  pec_inviata: { label: "PEC inviata", className: "border-amber-400 text-amber-600" },
  contratto_accettato: { label: "Contratto accettato", className: "border-sky-400 text-sky-600" },
  attiva: { label: "Attiva", className: "border-emerald-400 text-emerald-700" },
  rifiutata: { label: "Rifiutata", className: "border-red-300 text-red-600" },
  scaduta: { label: "Scaduta", className: "border-border text-muted-foreground" },
};

function StatoBadge({ stato }: { stato: string }) {
  const s = STATO_BADGE[stato] ?? { label: stato, className: "" };
  return (
    <Badge variant="outline" className={`whitespace-nowrap ${s.className}`}>
      {s.label}
    </Badge>
  );
}

const invitoLink = (token: string) => `${window.location.origin}/api/affiliazione/r/${token}`;

function EmptyState({
  icon: Icon, title, hint, tone = "neutral",
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  hint?: string;
  tone?: "neutral" | "ok";
}) {
  const toneCls = tone === "ok"
    ? "border-emerald-200 bg-emerald-50/60"
    : "border-dashed border-border bg-card/60";
  const iconCls = tone === "ok" ? "text-emerald-600" : "text-muted-foreground/60";
  return (
    <div className={`rounded-lg border px-6 py-10 text-center ${toneCls}`}>
      <Icon className={`mx-auto size-8 ${iconCls}`} />
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SectionCard({
  title, subtitle, actions, children,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Affiliazioni() {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Affiliazioni</h2>
        <p className="text-sm text-muted-foreground">
          Richieste di affiliazione verificata, inviti manager, funnel e anomalie.
        </p>
      </div>
      <Tabs defaultValue="richieste">
        {/* hover:text-primary sull'attivo: il trigger base ha hover:text-foreground
            che altrimenti vince sul verde quando il mouse resta sul tab cliccato */}
        <TabsList className="h-auto rounded-lg border border-border bg-card p-1 shadow-sm">
          {["richieste", "inviti", "kpi", "anomalie"].map((v) => (
            <TabsTrigger key={v}
              className="rounded-md px-4 py-1.5 capitalize data-active:bg-primary/10 data-active:text-primary data-active:hover:text-primary"
              value={v}>
              {v === "kpi" ? "KPI" : v}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="richieste" className="pt-2"><TabRichieste /></TabsContent>
        <TabsContent value="inviti" className="pt-2"><TabInviti /></TabsContent>
        <TabsContent value="kpi" className="pt-2"><TabKpi /></TabsContent>
        <TabsContent value="anomalie" className="pt-2"><TabAnomalie /></TabsContent>
      </Tabs>
    </div>
  );
}

function TabRichieste() {
  const [rows, setRows] = useState<AffRichiesta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [pecDraft, setPecDraft] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [pivaCopiata, setPivaCopiata] = useState<number | null>(null);

  const copiaPiva = async (r: AffRichiesta) => {
    const ok = await copyText(r.piva);
    if (ok) {
      setPivaCopiata(r.id);
      setTimeout(() => setPivaCopiata(null), 2500);
    } else {
      setWarning(`Copia non riuscita: seleziona e copia a mano la P.IVA ${r.piva}`);
    }
  };

  const load = useCallback(() => {
    adminApi.affListaRichieste().then(setRows).catch(() => setError("Impossibile caricare le richieste."));
  }, []);
  useEffect(load, [load]);

  async function azione(id: number, fn: () => Promise<{ ok: boolean; warning?: string }>) {
    setBusy(id);
    setError(null);
    setWarning(null);
    try {
      const out = await fn();
      if (out.warning) setWarning(out.warning);
      load();
    } catch {
      setError("Azione non riuscita.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-destructive">{error}</p>}
      {warning && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {warning}
        </p>
      )}
      {rows === null ? (
        !error && <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Nessuna richiesta di affiliazione."
          hint="Le richieste arrivano dai link invito: creane uno nel tab Inviti e consegnalo al manager."
        />
      ) : (
        <SectionCard
          title="Richieste di affiliazione"
          subtitle="Verifica l'azienda su INI-PEC, incolla la PEC ufficiale e invia il contratto."
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Attività</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead>Referente</TableHead>
                  <TableHead>Responsabile</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Numero</TableHead>
                  <TableHead>Creata</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="align-top">
                    <TableCell className="text-foreground font-medium">
                      {r.ragione_sociale}
                      <div className="text-xs text-muted-foreground font-normal mt-0.5">
                        {r.comune} ({r.provincia})
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="tabular-nums">{r.piva}</span>{" "}
                      <button
                        className={pivaCopiata === r.id ? "text-primary text-xs" : "underline text-muted-foreground text-xs"}
                        type="button"
                        title="Copia la P.IVA da incollare su INI-PEC"
                        onClick={() => copiaPiva(r)}
                      >
                        {pivaCopiata === r.id ? "copiata ✓" : "copia"}
                      </button>
                      <div className="mt-0.5">
                        <a className="text-primary underline text-xs" href={r.inipec_url}
                           target="_blank" rel="noreferrer">apri INI-PEC (imprese)</a>
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.nome_referente}
                      <div className="text-xs text-muted-foreground mt-0.5">{r.cellulare}</div>
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {r.responsabile ?? "Amministrazione"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <StatoBadge stato={r.stato} />
                        {r.attivo_kpi && (
                          <Badge variant="outline" className="border-emerald-400 text-emerald-700">
                            KPI attivo
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.numero_verificato ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 whitespace-nowrap">
                          <Check className="size-3.5" /> verificato
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {formatDateTime(r.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end gap-1.5">
                        {r.stato === "dati_inseriti" && (
                          <div className="flex gap-1.5">
                            <Input className="w-52" placeholder="PEC trovata su INI-PEC"
                              value={pecDraft[r.id] ?? ""}
                              onChange={(e) => setPecDraft({ ...pecDraft, [r.id]: e.target.value })} />
                            <Button size="sm" disabled={busy === r.id}
                              onClick={() => azione(r.id, () => adminApi.affConfermaPec(r.id, pecDraft[r.id] ?? ""))}>
                              Invia contratto
                            </Button>
                          </div>
                        )}
                        {r.stato === "pec_inviata" && (
                          <div className="flex flex-col items-end gap-1.5">
                            {r.pec && <span className="text-xs text-muted-foreground">{r.pec}</span>}
                            <Button size="sm" variant="outline" disabled={busy === r.id}
                              onClick={() => azione(r.id, () => adminApi.affReminder(r.id))}>
                              Reinvia (max 1)
                            </Button>
                            {/* correzione post-bounce: la PEC sbagliata può essere sostituita,
                                il backend riaccetta conferma_pec anche in pec_inviata */}
                            <div className="flex gap-1.5">
                              <Input className="w-52" placeholder="Correggi PEC (dopo bounce)"
                                value={pecDraft[r.id] ?? ""}
                                onChange={(e) => setPecDraft({ ...pecDraft, [r.id]: e.target.value })} />
                              <Button size="sm" variant="outline" disabled={busy === r.id}
                                onClick={() => azione(r.id, () => adminApi.affConfermaPec(r.id, pecDraft[r.id] ?? ""))}>
                                Correggi e invia
                              </Button>
                            </div>
                          </div>
                        )}
                        {r.stato === "contratto_accettato" && (
                          <Button size="sm" variant="outline" disabled={busy === r.id}
                            onClick={() => azione(r.id, () => adminApi.affProvisioningRetry(r.id))}>
                            Riprova provisioning
                          </Button>
                        )}
                        {!STATI_NON_RIFIUTABILI.has(r.stato) && (
                          <Button size="sm" variant="destructive" disabled={busy === r.id}
                            onClick={() => {
                              const motivo = window.prompt("Motivo del rifiuto?");
                              if (motivo) azione(r.id, () => adminApi.affRifiuta(r.id, motivo));
                            }}>
                            Rifiuta
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function TabInviti() {
  const [rows, setRows] = useState<AffInvito[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nome, setNome] = useState("");
  const [creating, setCreating] = useState(false);
  const [linkCopiato, setLinkCopiato] = useState<number | null>(null);

  const load = useCallback(() => {
    adminApi.affListaInviti().then(setRows).catch(() => setError("Impossibile caricare gli inviti."));
  }, []);
  useEffect(load, [load]);

  async function crea() {
    if (!nome.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await adminApi.affCreaInvito(nome.trim());
      setNome("");
      load();
    } catch {
      setError("Creazione invito non riuscita.");
    } finally {
      setCreating(false);
    }
  }

  async function toggle(i: AffInvito) {
    setError(null);
    try {
      await adminApi.affToggleInvito(i.id);
      load();
    } catch {
      setError("Aggiornamento invito non riuscito.");
    }
  }

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-destructive">{error}</p>}
      <SectionCard
        title="Link di invito"
        subtitle="Un link personale per manager: le richieste aperte da quel link restano attribuite a lui."
        actions={
          <div className="flex gap-2">
            <Input className="w-56" placeholder="Nome manager"
              value={nome} onChange={(e) => setNome(e.target.value)} />
            <Button size="sm" disabled={creating} onClick={crea}>+ Crea invito</Button>
          </div>
        }
      >
        {rows === null ? (
          !error && <p className="text-sm text-muted-foreground px-4 py-6">Caricamento...</p>
        ) : rows.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={Link2}
              title="Nessun invito creato."
              hint="Crea il primo invito e consegna il link (o il QR) all'area manager."
            />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((i) => (
              <li key={i.id}
                  className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${i.attivo ? "" : "opacity-60"}`}>
                <div className="min-w-0">
                  <div className="font-medium text-foreground flex items-center gap-2">
                    {i.nome}
                    {!i.attivo && (
                      <Badge variant="outline" className="text-muted-foreground">disattivato</Badge>
                    )}
                  </div>
                  <code className="text-xs text-muted-foreground break-all">{invitoLink(i.token)}</code>
                  <div className="text-xs text-muted-foreground mt-0.5">{i.responsabile ?? "Amministrazione"}</div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline"
                    onClick={async () => {
                      const ok = await copyText(invitoLink(i.token));
                      setLinkCopiato(ok ? i.id : null);
                      if (ok) setTimeout(() => setLinkCopiato(null), 2500);
                    }}>
                    {linkCopiato === i.id ? "Copiato ✓" : "Copia link"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(i)}>
                    {i.attivo ? "Disattiva" : "Riattiva"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function TabKpi() {
  const [rows, setRows] = useState<AffKpi[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    adminApi.affKpi().then(setRows).catch(() => setError("Impossibile caricare il funnel KPI."));
  }, []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (rows === null) return <p className="text-sm text-muted-foreground">Caricamento...</p>;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="Nessun manager con inviti."
        hint="Il funnel si popola appena un invito viene aperto dai candidati."
      />
    );
  }
  return (
    <SectionCard
      title="Funnel per manager"
      subtitle="Aperture del link → richieste → contratti firmati → affiliati attivi (il KPI che conta)."
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Manager</TableHead>
              <TableHead className="text-right">Aperture link</TableHead>
              <TableHead className="text-right">Richieste</TableHead>
              <TableHead className="text-right">Firmati</TableHead>
              <TableHead className="text-right">Attivi (KPI)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((k) => (
              <TableRow key={k.inviter_id}>
                <TableCell className="text-foreground font-medium">
                  {k.inviter}
                  {!k.attivo && <span className="text-muted-foreground font-normal"> · disattivato</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{k.aperture}</TableCell>
                <TableCell className="text-right tabular-nums">{k.richieste}</TableCell>
                <TableCell className="text-right tabular-nums">{k.firmati}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold text-emerald-700">{k.attivi}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SectionCard>
  );
}

function TabAnomalie() {
  const [data, setData] = useState<AffAnomalie | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    adminApi.affAnomalie().then(setData).catch(() => setError("Impossibile caricare le anomalie."));
  }, []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (data === null) return <p className="text-sm text-muted-foreground">Caricamento...</p>;

  const vuoto = data.ip_condivisi.length === 0
    && data.telegram_condivisi.length === 0
    && data.numeri_mismatch.length === 0
    && data.rete_non_entrata.length === 0;

  if (vuoto) {
    return (
      <EmptyState
        icon={ShieldCheck}
        tone="ok"
        title="Nessuna anomalia rilevata."
        hint="Nessun segnale di collusione tra le richieste (IP, account Telegram, numeri)."
      />
    );
  }

  return (
    <div className="space-y-3 text-sm">
      {data.ip_condivisi.map((a) => (
        <div key={a.ip} className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
          <ShieldAlert className="size-4 text-amber-600 mt-0.5 shrink-0" />
          <span>IP <code>{a.ip}</code> condiviso dalle richieste #{a.richieste.join(", #")}</span>
        </div>
      ))}
      {data.telegram_condivisi.map((a) => (
        <div key={a.telegram_user_id} className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
          <ShieldAlert className="size-4 text-amber-600 mt-0.5 shrink-0" />
          <span>Stesso account Telegram (<code>{a.telegram_user_id}</code>) su richieste #{a.richieste.join(", #")}</span>
        </div>
      ))}
      {data.numeri_mismatch.map((a) => (
        <div key={a.request_id} className="rounded-lg border border-border bg-card p-3 flex items-start gap-2">
          <ShieldAlert className="size-4 text-muted-foreground mt-0.5 shrink-0" />
          <span>Richiesta #{a.request_id}: numero dichiarato {a.dichiarato ?? "—"} ≠ verificato {a.verificato ?? "—"}</span>
        </div>
      ))}
      {data.rete_non_entrata.map((a) => (
        <div key={`rete-${a.request_id}-${a.tipo}`} className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
          <ShieldAlert className="size-4 text-amber-600 mt-0.5 shrink-0" />
          <span>Richiesta #{a.request_id} ({a.ragione_sociale}): attivata ma non entrata nella rete — {MOTIVO_RETE[a.tipo] ?? a.tipo}. Assegna l'agenzia da Rete → Agenzie.</span>
        </div>
      ))}
    </div>
  );
}
